import { Endgame, Modes, fusableGroups, freeRoutes, researchSummary } from "@highmarch/app";
import { dailyUnlocked, raiseCap, raiseCostAt, trialUnlocked } from "@highmarch/domain";
import type { HubExtras } from "../ui/guide/guide";
import { setHubExtras } from "../ui/hub/hubGuide";
import { sellRows } from "../ui/hub/marketModel";
import { earnHow } from "../ui/hub/eventLook";
import { eventClock } from "./events";
import { recordsAny } from "./records";
import { trophiesHeld } from "./trophyHall";
import type { Feature } from "./registry";

/**
 * Captain Bram's view of the features (FINISH_PLAN WP9): the facts his just-in-time hub lines read (HubFacts.extras,
 * guide.ts HubExtras), built from the catalog and the registry on each hub render. It only reads: every rule here is
 * the feature's own (the events' clock, the Trial's unlock, the Market's sell list).
 */

/** The More rows whose red count Bram reads (WP7: rewards to claim). Others are left alone: a dot can do work. */
const DOT_ROWS: readonly string[] = ["achievements"];

export const guideFeature: Feature = {
  id: "guide",
  register(ctx, registry) {
    const endgame = new Endgame(ctx.catalog);
    const modes = new Modes(ctx.catalog);
    const now = eventClock(() => ctx.now(), ctx.debug, typeof location === "undefined" ? "" : location.search);
    /** Gear v2: essence held, whether a worn item can be raised now, the FUSE groups and the free rewards waiting. */
    const forgeFacts = (t: number) => {
      const p = ctx.catalog.profile, B = ctx.balance;
      const raisable = p.gear.some((g) => g.equippedTo !== null && g.level < raiseCap(p, g) && raiseCostAt(B, g.rarity, g.level) <= p.forge.essence);
      return { essence: p.forge.essence, raisable, fusable: fusableGroups(p).length, freeRewards: freeRoutes(ctx.catalog, t).claimable };
    };
    setHubExtras((): HubExtras => {
      const hc = ctx.catalog.highestLevelCleared, t = now();
      const rows = registry.moreRows().filter((r) => r.visible?.() ?? true);
      const moreDots: Record<string, number> = {};
      for (const r of rows) if (r.dot && DOT_ROWS.includes(r.id)) moreDots[r.id] = r.dot();
      const e = endgame.open ? endgame.live(t) : null;
      return {
        moreRows: rows.map((r) => r.id),
        moreDots,
        event: e ? { name: e.name, currency: e.currency, how: earnHow(ctx.balance, e) } : null,
        trialOpen: trialUnlocked(ctx.balance.events, hc),
        heroicOpen: endgame.heroicWaiting().length > 0,
        tributeUnlocked: ctx.catalog.economy.tribute.unlocked(hc),
        marketSellable: sellRows(ctx.catalog).rows.length,
        marks: ctx.catalog.economy.wallet.marks,
        ironOpen: modes.ironUnlocked,
        endlessOpen: modes.endlessOpen,
        endlessChest: modes.endlessOpen && modes.pendingChest(t) !== null,
        dailyOpen: dailyUnlocked(ctx.balance.modes.daily, hc),
        recordsAny: recordsAny(),
        challengeOpen: hc >= ctx.balance.modes.challenge.unlockAfterLevel,
        trophies: trophiesHeld(),
        forge: forgeFacts(t),
        research: (({ available, affordable }) => ({ available, affordable }))(researchSummary(ctx.catalog.profile)),
      };
    });
  },
};
